// lib/koi-pond-v3/particles/fallingPetals.ts
//
// T10.2 — falling petals + leaves. Every 15–30s one drifts down on a
// sinusoidal, tumbling path: 70% cherry blossom (soft pink, small, slow
// tumble) / 30% maple leaf (warm amber, larger, more tumble). On touching
// the water it spawns an expanding ripple AND converts to a floating
// petal that drifts with the current and fades over 10–15s.
//
// BOUNDED POOLS — petals and ripples are fixed-size pools of reused
// meshes (state machine: idle → fall → float → idle). Nothing is ever
// created after init, so a long session can't accumulate stray petals.
// The slow spawn rate keeps only ~1–2 active at a time; the pool is sized
// well above that.

import {
  PlaneGeometry,
  ShaderMaterial,
  Mesh,
  Group,
  Color,
  Vector3,
  DoubleSide,
  AdditiveBlending,
} from 'three';
import { palette } from '../palette';

const WATER_Y = 1.0;
const PETAL_POOL = 14;
const RIPPLE_POOL = 10;
const FALL_SPEED = 0.5; // world units / sec
const SPAWN_Y = 3.0; // start height (above the water)

// soft-edged ellipse petal/leaf
function makePetalMaterial(): ShaderMaterial {
  return new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: DoubleSide,
    uniforms: {
      uColor: { value: new Color('#ffffff') },
      uOpacity: { value: 0 },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      precision highp float;
      uniform vec3 uColor;
      uniform float uOpacity;
      varying vec2 vUv;
      void main() {
        vec2 d = vUv - 0.5;
        // teardrop-ish ellipse (narrower in y → petal shape)
        float e = (d.x * d.x) / 0.25 + (d.y * d.y) / 0.16;
        float a = smoothstep(1.0, 0.72, e) * uOpacity;
        gl_FragColor = vec4(uColor, a);
      }
    `,
  });
}

// expanding concentric ring on the water surface
function makeRippleMaterial(): ShaderMaterial {
  return new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
    uniforms: {
      uRadius: { value: 0 }, // 0..1 expansion
      uOpacity: { value: 0 },
      uColor: { value: new Color(palette.rimLight.hex) },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      precision highp float;
      uniform float uRadius;
      uniform float uOpacity;
      uniform vec3 uColor;
      varying vec2 vUv;
      void main() {
        float r = length(vUv - 0.5) * 2.0; // 0 center .. ~1.41 corner
        // a thin ring that rides outward at uRadius, plus a fainter trailing one
        float ring = exp(-pow((r - uRadius) * 9.0, 2.0));
        float ring2 = exp(-pow((r - uRadius * 0.6) * 11.0, 2.0)) * 0.4;
        float a = (ring + ring2) * uOpacity;
        gl_FragColor = vec4(uColor, a);
      }
    `,
  });
}

interface Petal {
  mesh: Mesh;
  mat: ShaderMaterial;
  state: 'idle' | 'fall' | 'float';
  isMaple: boolean;
  vx: number;
  vz: number;
  swayPhase: number;
  spin: Vector3; // tumble axis*rate while falling
  age: number; // seconds since landing (float state)
  floatDur: number;
}

interface Ripple {
  mesh: Mesh;
  mat: ShaderMaterial;
  active: boolean;
  age: number;
  dur: number;
}

const rand = (a: number, b: number) => a + Math.random() * (b - a);

export function makeFallingPetals(fast = false) {
  const SPAWN_MIN = fast ? 3 : 15;
  const SPAWN_MAX = fast ? 5 : 30;
  const group = new Group();
  const petals: Petal[] = [];
  const ripples: Ripple[] = [];

  for (let i = 0; i < PETAL_POOL; i++) {
    const mat = makePetalMaterial();
    const mesh = new Mesh(new PlaneGeometry(1, 1), mat);
    mesh.visible = false;
    mesh.renderOrder = 4;
    group.add(mesh);
    petals.push({
      mesh, mat, state: 'idle', isMaple: false, vx: 0, vz: 0,
      swayPhase: 0, spin: new Vector3(), age: 0, floatDur: 12,
    });
  }
  for (let i = 0; i < RIPPLE_POOL; i++) {
    const mat = makeRippleMaterial();
    const mesh = new Mesh(new PlaneGeometry(1.6, 1.6), mat);
    mesh.rotation.x = -Math.PI / 2; // flat on the water
    mesh.visible = false;
    mesh.renderOrder = 2;
    group.add(mesh);
    ripples.push({ mesh, mat, active: false, age: 0, dur: 2.4 });
  }

  let lastT = -1;
  let nextSpawn = 4; // first petal a few seconds in

  function spawnPetal() {
    const p = petals.find((x) => x.state === 'idle');
    if (!p) return; // pool exhausted → skip (bounded)
    p.isMaple = Math.random() > 0.7; // 30% maple
    const size = p.isMaple ? rand(0.22, 0.28) : rand(0.11, 0.15);
    p.mesh.scale.setScalar(size);
    p.mat.uniforms.uColor.value.set(p.isMaple ? palette.mapleLeaf.hex : palette.cherryPetal.hex);
    p.mat.uniforms.uOpacity.value = 1;
    p.mesh.position.set(rand(-2.6, 3.2), SPAWN_Y, rand(-0.5, 3.6));
    p.mesh.rotation.set(rand(0, 6.28), rand(0, 6.28), rand(0, 6.28));
    p.vx = rand(-0.12, 0.12);
    p.vz = rand(-0.1, 0.1);
    p.swayPhase = rand(0, 6.28);
    const tumble = p.isMaple ? rand(1.2, 2.2) : rand(0.4, 0.9); // maple tumbles more
    p.spin.set(rand(-1, 1), rand(-1, 1), rand(-1, 1)).normalize().multiplyScalar(tumble);
    p.age = 0;
    p.floatDur = rand(10, 15);
    p.state = 'fall';
    p.mesh.visible = true;
  }

  function spawnRipple(x: number, z: number) {
    const r = ripples.find((x2) => !x2.active);
    if (!r) return;
    r.active = true;
    r.age = 0;
    r.mesh.position.set(x, WATER_Y + 0.02, z);
    r.mesh.visible = true;
  }

  function update(t: number) {
    let dt = lastT < 0 ? 0 : t - lastT;
    lastT = t;
    if (dt > 0.05) dt = 0.05;

    if (t >= nextSpawn) {
      spawnPetal();
      nextSpawn = t + rand(SPAWN_MIN, SPAWN_MAX);
    }

    for (const p of petals) {
      if (p.state === 'fall') {
        const sway = Math.sin(t * 1.3 + p.swayPhase) * 0.18;
        p.mesh.position.x += (p.vx + sway) * dt;
        p.mesh.position.z += p.vz * dt;
        p.mesh.position.y -= FALL_SPEED * dt;
        p.mesh.rotation.x += p.spin.x * dt;
        p.mesh.rotation.y += p.spin.y * dt;
        p.mesh.rotation.z += p.spin.z * dt;
        if (p.mesh.position.y <= WATER_Y) {
          // touchdown → ripple + convert to floating, exactly in sync
          spawnRipple(p.mesh.position.x, p.mesh.position.z);
          p.mesh.position.y = WATER_Y + 0.015;
          p.mesh.rotation.set(-Math.PI / 2, 0, Math.random() * 6.28); // lie flat
          p.state = 'float';
          p.age = 0;
        }
      } else if (p.state === 'float') {
        p.age += dt;
        // drift with a gentle "current" + slow spin on the surface
        p.mesh.position.x += (0.05 + p.vx * 0.3) * dt;
        p.mesh.position.z += p.vz * 0.3 * dt;
        p.mesh.rotation.z += 0.15 * dt;
        // hold, then fade over the back half of the float life
        const k = p.age / p.floatDur;
        p.mat.uniforms.uOpacity.value = Math.max(0, 1 - Math.max(0, (k - 0.5) / 0.5));
        if (p.age >= p.floatDur) {
          p.state = 'idle';
          p.mesh.visible = false;
        }
      }
    }

    for (const r of ripples) {
      if (!r.active) continue;
      r.age += dt;
      const k = r.age / r.dur;
      r.mat.uniforms.uRadius.value = k; // 0..1 expand
      r.mat.uniforms.uOpacity.value = Math.max(0, 1 - k) * 0.7;
      if (r.age >= r.dur) {
        r.active = false;
        r.mesh.visible = false;
      }
    }
  }

  function dispose() {
    for (const p of petals) {
      p.mat.dispose();
      p.mesh.geometry.dispose();
    }
    for (const r of ripples) {
      r.mat.dispose();
      r.mesh.geometry.dispose();
    }
  }

  return { object: group, update, dispose };
}
